import {
  createContext,
  forwardRef,
  useContext,
  useId,
  useState,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
  type TextareaHTMLAttributes,
} from 'react'
import { CheckCircle2, Circle, Eye, EyeOff, XCircle } from './icons.tsx'
import { cx } from './cx.ts'

const base =
  'border border-border bg-surface placeholder:text-subtle ' +
  'focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 disabled:bg-soft disabled:text-muted'
const control = `${base} w-full rounded-xl px-3 py-2 text-sm pos:min-h-12`
/** Small inline controls (inside a cart line, a table cell). */
const dense = `${base} rounded-lg px-2 py-1 text-xs`

export interface FieldProps {
  label: string
  hint?: ReactNode
  error?: string | null
  children: (id: string) => ReactNode
  className?: string
  /** Shown at the right of the label, e.g. a "Forgot password?" link. */
  labelAction?: ReactNode
}

/** Lets Input/Select/Textarea pick up their Field's hint or error for screen readers. */
const FieldContext = createContext<{ id: string; noteId?: string; invalid: boolean } | null>(null)

function useFieldAria(id: string | undefined, describedBy: string | undefined) {
  const field = useContext(FieldContext)
  if (!field || !id || id !== field.id) return {}
  return {
    'aria-describedby': describedBy ?? field.noteId,
    'aria-invalid': field.invalid || undefined,
  }
}

/** Label + control + hint/error, with ids wired for accessibility. */
export function Field({ label, hint, error, children, className, labelAction }: FieldProps) {
  const id = useId()
  const note = error || hint
  const noteId = note ? `${id}-note` : undefined
  return (
    <div className={className}>
      {labelAction ? (
        <div className="mb-1 flex items-baseline justify-between gap-2">
          <label htmlFor={id} className="block text-sm font-medium text-muted">
            {label}
          </label>
          {labelAction}
        </div>
      ) : (
        <label htmlFor={id} className="mb-1 block text-sm font-medium text-muted">
          {label}
        </label>
      )}
      <FieldContext.Provider value={{ id, noteId, invalid: !!error }}>
        {children(id)}
      </FieldContext.Provider>
      {error ? (
        <p id={noteId} className="mt-1 text-xs text-danger">
          {error}
        </p>
      ) : hint ? (
        <p id={noteId} className="mt-1 text-xs text-subtle">
          {hint}
        </p>
      ) : null}
    </div>
  )
}

export const Input = forwardRef<
  HTMLInputElement,
  InputHTMLAttributes<HTMLInputElement> & { dense?: boolean }
>(function Input({ className, dense: small, ...rest }, ref) {
  const aria = useFieldAria(rest.id, rest['aria-describedby'])
  return <input ref={ref} className={cx(small ? dense : control, className)} {...aria} {...rest} />
})

/** A password box with a button to show or hide what was typed. */
export const PasswordInput = forwardRef<
  HTMLInputElement,
  Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>
>(function PasswordInput({ className, ...rest }, ref) {
  const [visible, setVisible] = useState(false)
  const aria = useFieldAria(rest.id, rest['aria-describedby'])
  const Icon = visible ? EyeOff : Eye
  return (
    <div className="relative">
      <input
        ref={ref}
        type={visible ? 'text' : 'password'}
        className={cx(control, 'pr-20', className)}
        autoCapitalize="none"
        spellCheck={false}
        {...aria}
        {...rest}
      />
      <button
        type="button"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
        aria-pressed={visible}
        title={visible ? 'Hide password' : 'Show password'}
        className="absolute inset-y-1 right-1 flex items-center gap-1 rounded-lg px-2 text-xs font-semibold text-primary hover:bg-primary-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
      >
        <Icon className="size-4" aria-hidden />
        {visible ? 'Hide' : 'Show'}
      </button>
    </div>
  )
})

export interface PasswordStrengthProps {
  /** How many of the four segments are lit. */
  bars: number
  /** The word beside the bar ("Strong"). */
  word: string
  tone: 'danger' | 'warning' | 'success'
  /** Why the password is refused; announced when it changes. */
  reason?: string | null
  /** What a password must be, each met, not met, or not judged yet. */
  checks: { label: string; state: 'yes' | 'no' | 'wait' }[]
  className?: string
}

const STRENGTH_TONE = {
  danger: { bar: 'bg-danger', text: 'text-danger' },
  warning: { bar: 'bg-warning', text: 'text-warning' },
  success: { bar: 'bg-success', text: 'text-success' },
}
const CHECK_LOOK = {
  yes: { Icon: CheckCircle2, text: 'text-success' },
  no: { Icon: XCircle, text: 'text-danger' },
  wait: { Icon: Circle, text: 'text-muted' },
}

/** Under a new password: how strong it is, and what it still needs. */
export function PasswordStrength({
  bars,
  word,
  tone,
  reason,
  checks,
  className,
}: PasswordStrengthProps) {
  const look = STRENGTH_TONE[tone]
  return (
    <div className={cx('mt-2', className)}>
      <div className="flex items-center gap-2.5">
        <div className="flex flex-1 gap-1" aria-hidden>
          {[0, 1, 2, 3].map((i) => (
            <span
              key={i}
              className={cx(
                'h-1 flex-1 rounded-full transition-colors duration-150',
                i < bars ? look.bar : 'bg-border',
              )}
            />
          ))}
        </div>
        <span className={cx('min-w-16 whitespace-nowrap text-right text-xs font-bold', look.text)}>
          {word}
        </span>
      </div>
      <p role="alert" className="mt-1.5 text-xs font-semibold text-danger empty:hidden">
        {reason}
      </p>
      <ul className="mt-2 space-y-1 text-xs">
        {checks.map(({ label, state }) => {
          const { Icon, text } = CHECK_LOOK[state]
          return (
            <li key={label} className={cx('flex items-center gap-1.5', text)}>
              <Icon className="size-4 shrink-0" aria-hidden />
              {label}
              <span className="sr-only">
                {state === 'yes' ? ': done' : state === 'no' ? ': not yet' : ''}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

export function Select({
  className,
  children,
  dense: small,
  ...rest
}: SelectHTMLAttributes<HTMLSelectElement> & { dense?: boolean }) {
  const aria = useFieldAria(rest.id, rest['aria-describedby'])
  return (
    <select className={cx(small ? dense : control, 'pr-8', className)} {...aria} {...rest}>
      {children}
    </select>
  )
}

export function Textarea({ className, ...rest }: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  const aria = useFieldAria(rest.id, rest['aria-describedby'])
  return <textarea className={cx(control, 'min-h-20', className)} {...aria} {...rest} />
}

export function Checkbox({
  label,
  className,
  ...rest
}: InputHTMLAttributes<HTMLInputElement> & { label: ReactNode }) {
  return (
    <label className={cx('inline-flex cursor-pointer items-center gap-2 text-sm', className)}>
      <input type="checkbox" className="size-4 rounded border-border accent-primary" {...rest} />
      {label}
    </label>
  )
}

/**
 * An on/off setting shown as a card: a checkbox with role="switch", so it is
 * still named by its label and toggled with Space.
 */
export function Switch({
  label,
  description,
  className,
  ...rest
}: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> & {
  label: string
  description?: ReactNode
}) {
  const id = useId()
  return (
    <label
      className={cx(
        'flex cursor-pointer items-center justify-between gap-3 rounded-xl border border-border bg-surface px-3.5 py-3 transition-colors hover:border-primary/40 has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-primary/30 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-60',
        className,
      )}
    >
      <span className="min-w-0">
        <span className="block text-sm font-semibold text-ink">{label}</span>
        {description && (
          <span id={`${id}-d`} className="block text-xs text-muted">
            {description}
          </span>
        )}
      </span>
      <input
        type="checkbox"
        role="switch"
        className="peer sr-only"
        aria-describedby={description ? `${id}-d` : undefined}
        {...rest}
      />
      <span
        aria-hidden
        className="relative h-6 w-10 shrink-0 rounded-full bg-border transition-colors peer-checked:bg-primary after:absolute after:top-[3px] after:left-[3px] after:size-[18px] after:rounded-full after:bg-white after:shadow-card after:transition-transform peer-checked:after:translate-x-4"
      />
    </label>
  )
}
